import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, mediaUrl } from "../lib/api";
import { toast } from "sonner";
import { CalendarClock, Send, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";

export default function Calendar() {
  const qc = useQueryClient();
  const { data: pubs = [] } = useQuery({ queryKey: ["publications"], queryFn: () => api.get("/publishing/schedule").then(r => r.data) });
  const { data: content = [] } = useQuery({ queryKey: ["content"], queryFn: () => api.get("/studio/content").then(r => r.data) });
  const { data: conn } = useQuery({ queryKey: ["pub-connections"], queryFn: () => api.get("/publishing/connections").then(r => r.data) });
  const real = !!conn?.real_publishing_available;

  const publish = async (id) => {
    try {
      const { data } = await api.post(`/publishing/${id}/simulate-publish`);
      toast.message(`Demo only — nothing was posted. ${data.metrics.impressions.toLocaleString()} impressions are simulated.`);
      qc.invalidateQueries({ queryKey: ["publications"] });
      qc.invalidateQueries({ queryKey: ["content"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
      qc.invalidateQueries({ queryKey: ["platform-perf"] });
      qc.invalidateQueries({ queryKey: ["top-content"] });
      qc.invalidateQueries({ queryKey: ["retention-curve"] });
    } catch { toast.error("Publish failed"); }
  };
  const cancel = async (id) => {
    await api.delete(`/publishing/${id}`);
    qc.invalidateQueries({ queryKey: ["publications"] });
  };

  const byDay = {};
  pubs.forEach(p => {
    const d = new Date(p.scheduled_at).toDateString();
    (byDay[d] ||= []).push(p);
  });

  return (
    <div className="min-h-screen">
      <div className="border-b border-[#141414] px-8 py-5 sticky top-0 bg-[#050505]/90 backdrop-blur-md z-20">
        <div className="font-mono text-[10px] uppercase tracking-widest text-[#52525B]">// Publishing Fleet</div>
        <h1 data-testid="publishing-mode" className="font-display font-bold text-2xl">{pubs.length} scheduled · {real ? "Real publishing connected" : "Not connected — demo mode, nothing is posted"}</h1>
      </div>

      <div className="p-8">
        <div className="mb-4 p-3 border border-[#F5A524]/30 bg-[#F5A524]/5 text-xs text-[#F5A524] rounded-sm">
          <span className="font-mono uppercase tracking-widest">Connection required</span> — to post for real, connect a Facebook Page (permission pages_manage_posts) and the Instagram professional account linked to it (instagram_content_publish). Until then “Run demo” only simulates a post: results are marked DEMO, never “published”, and metrics are synthetic.
        </div>

        <div className="space-y-6">
          {Object.entries(byDay).map(([day, items]) => (
            <div key={day}>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5A524] mb-2">{day}</div>
              <div className="border border-[#171717] bg-[#0a0a0a]">
                {items.map(p => {
                  const c = content.find(x => x.id === p.content_id);
                  return (
                    <div key={p.id} data-testid={`pub-${p.id}`} className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-[#141414] last:border-0 hover:bg-[#0f0f0f] transition-colors items-center">
                      <div className="col-span-2">
                        <div className="w-full aspect-video bg-[#0f0f0f] border border-[#171717] overflow-hidden">
                          {c?.thumbnail_url ? <img src={mediaUrl(c.thumbnail_url)} alt="" className="w-full h-full object-cover" /> : null}
                        </div>
                      </div>
                      <div className="col-span-4">
                        <div className="text-sm font-medium truncate">{c?.title || "—"}</div>
                        <div className="font-mono text-[9px] uppercase tracking-widest text-[#52525B] mt-0.5">{p.account_handle}</div>
                      </div>
                      <div className="col-span-2">
                        <div className="font-mono text-[10px] uppercase tracking-widest text-[#00F0FF]">{p.platform}</div>
                        <div className="font-mono text-[10px] text-[#A1A1AA]">{new Date(p.scheduled_at).toLocaleTimeString()}</div>
                      </div>
                      <div className="col-span-2 font-mono text-[10px] uppercase tracking-widest">
                        <span className={
                          p.status === "published" ? "text-[#17C964]" :
                          p.status === "failed" ? "text-[#F31260]" : "text-[#F5A524]"
                        } data-testid={`pub-status-${p.id}`}>{p.status === "simulated" || (p.simulated && p.status === "published") ? "DEMO · not posted" : p.status}</span>
                      </div>
                      <div className="col-span-2 flex justify-end gap-1">
                        {p.status === "scheduled" ? (
                          <>
                            <Button data-testid={`pub-publish-${p.id}`} onClick={() => publish(p.id)} size="sm" className="bg-[#F5A524] hover:bg-[#D98218] text-black text-xs rounded-sm h-8">
                              <Send className="w-3 h-3 mr-1" /> {real ? "Publish now" : "Run demo (no post)"}
                            </Button>
                            <Button data-testid={`pub-cancel-${p.id}`} onClick={() => cancel(p.id)} variant="ghost" size="sm" className="text-[#F31260] hover:bg-[#F31260]/10 h-8">
                              <XCircle className="w-3 h-3" />
                            </Button>
                          </>
                        ) : (
                          <div className="text-right">
                            <div className="font-mono text-[10px] text-[#00F0FF]">{p.metrics?.impressions?.toLocaleString() || "—"} imp{p.simulated ? " · demo" : ""}</div>
                            <div className="font-mono text-[9px] text-[#52525B]">{p.metrics?.watch_through_pct ?? "—"}% w/t</div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {pubs.length === 0 && (
            <div className="border border-dashed border-[#222] bg-[#0a0a0a] p-16 text-center">
              <CalendarClock className="w-8 h-8 text-[#52525B] mx-auto mb-3" />
              <div className="font-display font-bold text-lg mb-1">Nothing on the fleet yet.</div>
              <div className="text-sm text-[#A1A1AA]">Approve content in the Studio and schedule it from there.</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
